<template>
  <Modal
    v-model:visible="isVisible"
    class="waterfall-tender-modal"
    :class="{ 'performing-setup': performingSetup }"
    :dismissable-mask="false"
    :header="t('waterfallTender.waterfallTendering')"
    size="md"
    maximizable
    @keypress.esc="onCancel"
  >
    <template v-if="performingSetup">
      <div>
        <LoadingIndicator loading />
        <p class="font-italic">
          {{ t('waterfallTender.preparingWaterfallTenderConfig') }}
        </p>
      </div>
    </template>
    <template v-else>
      <p>{{ t('waterfallTender.modalDescription') }}</p>

      <Message
        v-if="shouldShowEmailRequiredWarning"
        severity="warn"
        class="rygen-message"
        closable
      >
        {{ t('waterfallTender.emailRequiredWarning') }}
      </Message>

      <ol ref="includedRatesList" class="waterfall-tender-rates-list included">
        <li
          v-for="(rateQuote, index) in config.quotes"
          :key="rateQuote?.rateQuote?.id ?? -1"
          class="flex flex-gap-1 align-items-center"
        >
          <div class="marker">{{ index + 1 }}.</div>
          <WaterfallTenderRateQuoteCard
            v-model:tender-config="rateQuote.tenderConfigOptions"
            v-model:send-to-brokerage-request="rateQuote.sendToBrokerageRequest"
            :tender-method="rateQuote.tenderMethod"
            :rate-quote="rateQuote.rateQuote"
            :broker-carrier-setting="rateQuote.brokerCarrierSetting"
            :send-to-brokerage-carriers="sendToBrokerageCarriers"
            :equipment-types="equipmentTypes"
            :service-levels="serviceLevels"
            :enable-up-arrow="index > 0"
            :enable-down-arrow="index < config.quotes.length - 1"
            :available-tender-methods="
              getAvailableTenderMethods(rateQuote.rateQuote)
            "
            class="flex-grow-1"
            @update:tender-method="onTenderMethodChange(rateQuote, $event)"
            @move-up="onMoveUp(rateQuote)"
            @move-down="onMoveDown(rateQuote)"
          />
          <div class="action-wrapper">
            <RygenButton
              mode="tertiary"
              icon="pi pi-times"
              :aria-label="t('common.remove')"
              @click="excludeRate(rateQuote)"
            />
          </div>
        </li>
      </ol>

      <p v-if="!config.quotes.length" class="font-italic text-center">
        {{ t('waterfallTender.noRatesSelectedForWaterfallTendering') }}
      </p>

      <Divider />

      <h2 class="section-subheader">
        {{ t('waterfallTender.excludedRates') }}
      </h2>
      <ul class="waterfall-tender-rates-list excluded">
        <li
          v-for="excludedQuote in excludedRateQuotesToDisplay"
          :key="excludedQuote.rateQuote?.id ?? -1"
          class="flex flex-gap-1 align-items-center"
        >
          <div class="marker" />
          <WaterfallTenderRateQuoteCard
            v-model:tender-config="excludedQuote.tenderConfigOptions"
            :rate-quote="excludedQuote.rateQuote"
            :tender-method="excludedQuote.tenderMethod"
            :exclusion-reason="excludedQuote.reason"
            excluded
            class="flex-grow-1"
          />
          <div class="action-wrapper">
            <RygenButton
              v-if="
                excludedQuote.reason ===
                  WaterfallTenderRateQuoteExclusionReason.MANUAL_EXCLUSION ||
                excludedQuote.reason ===
                  WaterfallTenderRateQuoteExclusionReason.EXCLUDED_BY_ROUTING_GUIDE
              "
              mode="tertiary"
              :aria-label="t('waterfallTender.include')"
              @click="includeRate(excludedQuote)"
            >
              {{ t('waterfallTender.include') }}
            </RygenButton>
          </div>
        </li>
      </ul>

      <p
        v-if="!excludedRateQuotesToDisplay.length"
        class="font-italic text-center"
        :style="{ 'padding-bottom': 'var(--sp-3)' }"
      >
        {{ t('waterfallTender.noExcludedRateQuotes') }}
      </p>
    </template>

    <template #footer>
      <InputWrapper
        v-slot="{ ariaProps, inputId }"
        input-id="waterfall-tender-timeout-duration"
        :label="t('waterfallTender.tenderTimeoutDuration.timeOutAfter')"
        inline
      >
        <DurationSelect
          :id="inputId"
          v-model="config.tenderTimeoutDuration"
          v-bind="ariaProps"
          :possible-durations="timeoutOptions"
          :placeholder="t('waterfallTender.tenderTimeoutDuration.timeOutAfter')"
          :disabled="performingSetup"
          append-to="body"
        />
      </InputWrapper>

      <div class="flex flex-gap-1 align-items-center">
        <RygenButton
          mode="tertiary"
          :label="t('common.cancel')"
          @click="onCancel"
        />
        <RygenButton
          :disabled="performingSetup || config.quotes.length === 0"
          :loading="startingWaterfallTender"
          mode="primary"
          icon="pi pi-send"
          :label="t('shipments.tender')"
          @click="onBeginWaterfallTender"
        />
      </div>
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { useFlag } from '@unleash/proxy-client-vue';
import { useSortable } from '@vueuse/integrations/useSortable';
import { Duration } from 'luxon';
import type { PropType, Ref } from 'vue';
import { computed, ref, toRefs, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import {
  minLength,
  required,
  useUid,
  useValidations,
} from '@rygen/rygen-ui/composables';
import {
  CapacityProviderType,
  CarrierSetting,
  ExpirationDuration,
  Loadable,
  SendToBrokerageRequest,
  TenderConfig,
  TenderConfigOptions,
  TenderMethod,
} from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import api from '@/api';
import { useRoutingGuide } from '@/composables';
import {
  Carrier,
  RateQuote,
  RateShop,
  RateUsage,
  WaterfallTenderConfig,
  WaterfallTenderExcludedRateQuote,
  WaterfallTenderRateQuoteConfig,
  WaterfallTenderRateQuoteExclusionReason,
} from '@/model';
import { useEquipmentTypeStore } from '@/stores/EquipmentType';
import { useServiceLevelStore } from '@/stores/ServiceLevel';

import WaterfallTenderRateQuoteCard from '@/components/rateShop/WaterfallTenderRateQuoteCard.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import DurationSelect from '@rygen/rygen-ui/components/form/DurationSelect.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import LoadingIndicator from '@rygen/rygen-ui/components/ui/LoadingIndicator.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import Divider from 'primevue/divider';
import Message from 'primevue/message';

type WaterfallTenderConfigVm = {
  quotes: WaterfallTenderRateQuoteVm[];
  excludedQuotes: WaterfallTenderExcludedRateQuoteVm[];
  tenderTimeoutDuration: Duration;
};

type WaterfallTenderRateQuoteVm = {
  rateQuote: RateQuote;
  tenderMethod: TenderMethod;
  tenderConfigOptions: TenderConfigOptions;
  sendToBrokerageRequest?: SendToBrokerageRequest;
  brokerCarrierSetting?: CarrierSetting;
};

type WaterfallTenderExcludedRateQuoteVm = WaterfallTenderRateQuoteVm & {
  reason: WaterfallTenderRateQuoteExclusionReason;
};

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  rateShop: {
    type: Object as PropType<Loadable<RateShop>>,
    required: true,
  },
});
const { rateShop } = toRefs(props);
const emit = defineEmits<{
  (
    event: 'waterfall-tender',
    data: {
      configuration: WaterfallTenderConfig;
      callback: (success: boolean) => void;
    },
  ): void;
}>();

const includedRatesList = useTemplateRef('includedRatesList');

const config = ref<WaterfallTenderConfigVm>({
  quotes: [],
  excludedQuotes: [],
  tenderTimeoutDuration: Duration.fromISO(ExpirationDuration.PT1H),
});

const isDoNotDispatchEnabled = useFlag('doNotDispatch');
const { t } = useI18n();
const { excludedRates: routingGuideExcludedRates } = useRoutingGuide(
  rateShop,
  ref({}),
);
const { uid } = useUid('waterfall-tender-config-modal');
const { validate } = useValidations(
  config,
  {
    quotes: {
      minLength: minLength(1, t('rateShop.quote', 2)),
    },
    tenderTimeoutDuration: {
      required: required(
        t('waterfallTender.tenderTimeoutDuration.timeOutAfter'),
      ),
    },
  },
  uid,
);

const timeoutOptions: ExpirationDuration[] = [
  ExpirationDuration.PT30M,
  ExpirationDuration.PT1H,
  ExpirationDuration.PT1H30M,
];

const { equipmentTypes, loadEquipmentTypes } = useEquipmentTypeStore();
const { serviceLevels, loadServiceLevels } = useServiceLevelStore();

const startingWaterfallTender = ref(false);
const performingSetup = ref(false);
const sendToBrokerageCarriers = ref<Loadable<Carrier[]>>({ loading: false });
const clientCarrierDoNotDispatchMap: Ref<Map<string, boolean>> = ref(new Map());
const clientCarrierTenderConfigMap: Ref<Map<string, TenderConfig[]>> = ref(
  new Map(),
);
const clientCarrierSettingMap: Ref<Map<string, CarrierSetting>> = ref(
  new Map(),
);

const includedRates = computed({
  get: () => config.value.quotes,
  set: (newVal: WaterfallTenderRateQuoteVm[]) => {
    config.value.quotes = newVal;
  },
});

const excludedRateQuotesToDisplay = computed(() => {
  return config.value.excludedQuotes.filter(
    (excludedRateQuote) =>
      excludedRateQuote.reason !==
      WaterfallTenderRateQuoteExclusionReason.NOT_A_REAL_RATE,
  );
});

const shouldShowEmailRequiredWarning = computed(() => {
  return config.value.quotes.some((rq) => {
    if (rq.rateQuote.carrier?.type === CapacityProviderType.BROKER) {
      return (
        rq.sendToBrokerageRequest?.tenderMethod === TenderMethod.EMAIL &&
        !rq.sendToBrokerageRequest?.tenderConfigOptions?.emails?.length
      );
    }
    return (
      rq.tenderMethod === TenderMethod.EMAIL &&
      !rq.tenderConfigOptions?.emails?.length
    );
  });
});

const loadSendToBrokerageData = async () => {
  sendToBrokerageCarriers.value = { loading: true };
  try {
    const carriers = await api.carrier.findAllInContext();
    sendToBrokerageCarriers.value = { loading: false, data: carriers };
  } catch {
    sendToBrokerageCarriers.value = { loading: false };
  }
  loadEquipmentTypes();
  loadServiceLevels();
};

const loadCarrierTenderConfigs = async () => {
  if (!authorization.hasAuthority('client', 'read')) {
    return;
  }

  clientCarrierDoNotDispatchMap.value.clear();

  const allRateQuotes = rateShop.value.data?.rateQuotes ?? [];

  const carriers = new Set(
    allRateQuotes.filter(isRealRateQuote).map((rateQuote) => rateQuote.carrier),
  );

  let loadCarrierSettingPromises = [...carriers.values()]
    .filter(notNull)
    .map((carrier) => async (): Promise<void> => {
      const scac = carrier.scac as string;
      const carrierSettings =
        carrier.type === CapacityProviderType.CARRIER
          ? await api.carrier.findCarrierSettingsForScac(scac)
          : await api.broker.findBrokerSettingsForScac(scac);

      const tenderConfig = carrierSettings.tenderConfig ?? [];
      clientCarrierTenderConfigMap.value.set(scac, tenderConfig);

      if (carrier.type === CapacityProviderType.BROKER) {
        const filteredCarrierSetting = new CarrierSetting({
          ...carrierSettings,
          // Manual is not a valid waterfall tender method
          tenderConfig: carrierSettings.tenderConfig.filter(
            (config) => config.method !== TenderMethod.MANUAL,
          ),
        });

        clientCarrierSettingMap.value.set(scac, filteredCarrierSetting);
      }

      if (
        isDoNotDispatchEnabled.value &&
        !clientCarrierDoNotDispatchMap.value.has(scac)
      ) {
        clientCarrierDoNotDispatchMap.value.set(
          scac,
          carrierSettings.doNotDispatch,
        );
      }
    });

  const results = await Promise.allSettled(
    loadCarrierSettingPromises.map((promise) => promise()),
  );

  results.forEach((result) => {
    if (result.status === 'rejected') {
      // 404 is typical if no carrier settings are configured for client - I don't think we need to let the user know, but logging just in case
      console.warn('Failed to load carrier config for client', result.reason);
    }
  });
};

/**
 * Determines if the rate quote is a "real" rate quote, meaning it is not a benchmark or audit only
 */
const isRealRateQuote = (rateQuote: RateQuote) =>
  !rateQuote.auditOnly && rateQuote.rateUsage === RateUsage.RATE_SHOP;

/**
 * Loads the configured tender method based on the carrier settings
 */
const getTenderConfig = (rateQuote: RateQuote): TenderConfig[] => {
  const scac = rateQuote.carrier?.scac;
  if (!scac) {
    return [];
  }

  return clientCarrierTenderConfigMap.value.get(scac) ?? [];
};

/**
 * Loads the most recent configured doNotDispatch flag from the carrier settings
 */
const isCarrierDoNotDispatch = (rateQuote: RateQuote): boolean => {
  const scac = rateQuote.carrier?.scac;
  if (!scac) {
    return rateQuote.doNotDispatch;
  }

  // invalid key likely means the carrier/broker was removed from client domain
  return clientCarrierDoNotDispatchMap.value.get(scac) ?? false;
};

const getEffectiveTenderMethod = (
  rateQuote: WaterfallTenderRateQuoteVm,
): TenderMethod => {
  const isBroker =
    rateQuote.rateQuote.carrier?.type === CapacityProviderType.BROKER;

  const brokerTenderMethod =
    rateQuote.sendToBrokerageRequest?.tenderMethod ?? TenderMethod.MANUAL;

  return isBroker ? brokerTenderMethod : rateQuote.tenderMethod;
};

const getAvailableTenderMethods = (rateQuote: RateQuote): TenderMethod[] => {
  const tenderConfig = getTenderConfig(rateQuote);
  return (
    tenderConfig
      .filter((config) => config.method !== TenderMethod.MANUAL)
      .map((config) => config.method)
      .filter(notNull) ?? []
  );
};

const onTenderMethodChange = (
  rateQuote: WaterfallTenderRateQuoteVm,
  tenderMethod: TenderMethod,
) => {
  const tenderConfig = getTenderConfig(rateQuote.rateQuote);
  const previousOptions = tenderConfig.find(
    (config) => config.method === rateQuote.tenderMethod,
  )?.configOptions;
  const selectedOptions = tenderConfig.find(
    (config) => config.method === tenderMethod,
  )?.configOptions;

  rateQuote.tenderMethod = tenderMethod;

  const options = rateQuote.tenderConfigOptions;
  options.expiration = selectedOptions?.expiration;
  options.notes = selectedOptions?.notes;
  options.parcelLabelSize = selectedOptions?.parcelLabelSize;

  if (!options.emails || options.emails === previousOptions?.emails) {
    options.emails = selectedOptions?.emails;
  }
};

const initializeWaterfallTenderCandidates = async () => {
  if (!rateShop.value.data) {
    console.warn(
      'Waterfall Tender modal initialized but rate shop is not loaded',
    );
    config.value.quotes = [];
    config.value.excludedQuotes = [];
    return;
  }

  performingSetup.value = true;

  try {
    await loadCarrierTenderConfigs();

    const defaultInclusions: WaterfallTenderRateQuoteVm[] = [];
    const defaultExclusions: WaterfallTenderExcludedRateQuoteVm[] = [];

    rateShop.value.data.rateQuotes
      .map((rateQuote) => {
        const scac = rateQuote.carrier?.scac as string;
        const carrierConfig = getTenderConfig(rateQuote);
        const tenderMethod = getTenderMethodFromClientCarrier(carrierConfig);
        const isBroker =
          rateQuote.carrier?.type === CapacityProviderType.BROKER;
        const brokerCarrierSetting = isBroker
          ? clientCarrierSettingMap.value.get(scac)
          : undefined;
        const sendToBrokerageRequest = isBroker
          ? new SendToBrokerageRequest({
              tenderMethod: tenderMethod ?? undefined,
              tenderConfigOptions:
                carrierConfig.find((config) => config.method === tenderMethod)
                  ?.configOptions ?? undefined,
            })
          : undefined;

        return {
          rateQuote,
          tenderMethod:
            carrierConfig.find((config) => config.method === tenderMethod)
              ?.method ?? TenderMethod.MANUAL,
          tenderConfigOptions: new TenderConfigOptions(
            carrierConfig.find((config) => config.method === tenderMethod)
              ?.configOptions,
          ),
          sendToBrokerageRequest,
          brokerCarrierSetting,
        };
      })
      .forEach((rateQuote) => {
        const tenderMethod = getEffectiveTenderMethod(rateQuote);
        const eligibility = getWaterfallTenderEligibility(
          rateQuote.rateQuote,
          tenderMethod,
        );
        if (eligibility.eligible) {
          defaultInclusions.push(rateQuote);
        } else {
          defaultExclusions.push({
            ...rateQuote,
            reason: eligibility.reason,
          });
        }
      });

    config.value.quotes = defaultInclusions;
    config.value.excludedQuotes = defaultExclusions;
  } finally {
    performingSetup.value = false;
  }
};

const getTenderMethodFromClientCarrier = (tenderConfig: TenderConfig[]) => {
  const defaultMethod = tenderConfig.find(
    (config) => config.defaultMethod,
  )?.method;

  if (!(defaultMethod === TenderMethod.MANUAL)) {
    return defaultMethod;
  }
  return tenderConfig.find((config) => config.method !== TenderMethod.MANUAL)
    ?.method;
};

const getWaterfallTenderEligibility = (
  rateQuote: RateQuote,
  tenderMethod: TenderMethod,
):
  | { eligible: true; reason: undefined }
  | { eligible: false; reason: WaterfallTenderRateQuoteExclusionReason } => {
  if (!isRealRateQuote(rateQuote)) {
    return {
      eligible: false,
      reason: WaterfallTenderRateQuoteExclusionReason.NOT_A_REAL_RATE,
    };
  }

  if (
    routingGuideExcludedRates.value.some(
      (excludedRate) => excludedRate.id === rateQuote.id,
    )
  ) {
    return {
      eligible: false,
      reason: WaterfallTenderRateQuoteExclusionReason.EXCLUDED_BY_ROUTING_GUIDE,
    };
  }

  if (tenderMethod === TenderMethod.MANUAL) {
    return {
      eligible: false,
      reason: WaterfallTenderRateQuoteExclusionReason.UNSUPPORTED_TENDER_METHOD,
    };
  }

  if (isCarrierDoNotDispatch(rateQuote)) {
    return {
      eligible: false,
      reason: WaterfallTenderRateQuoteExclusionReason.DO_NOT_DISPATCH,
    };
  }

  return {
    eligible: true,
    reason: undefined,
  };
};

const excludeRate = (rateQuote: WaterfallTenderRateQuoteVm) => {
  const indexInInclusion = config.value.quotes.findIndex(
    (includedRateQuote) =>
      includedRateQuote.rateQuote.id === rateQuote.rateQuote.id,
  );
  if (indexInInclusion > -1) {
    config.value.quotes.splice(indexInInclusion, 1);
  }

  const tenderMethod = getEffectiveTenderMethod(rateQuote);
  const eligibility = getWaterfallTenderEligibility(
    rateQuote.rateQuote,
    tenderMethod,
  );

  const excludedRateQuote: WaterfallTenderExcludedRateQuoteVm = {
    ...rateQuote,
    reason:
      eligibility.reason ??
      WaterfallTenderRateQuoteExclusionReason.MANUAL_EXCLUSION,
  };

  config.value.excludedQuotes.unshift(excludedRateQuote);
};

const includeRate = (rateQuote: WaterfallTenderExcludedRateQuoteVm) => {
  const indexInExclusion = config.value.excludedQuotes.findIndex(
    (excludedRateQuote) =>
      excludedRateQuote.rateQuote.id === rateQuote.rateQuote.id,
  );
  if (indexInExclusion > -1) {
    config.value.excludedQuotes.splice(indexInExclusion, 1);
  }

  const includedRateQuote: WaterfallTenderRateQuoteVm = {
    ...rateQuote,
  };

  config.value.quotes.push(includedRateQuote);
};

const onMoveUp = (rateQuote: WaterfallTenderRateQuoteVm) => {
  const index = config.value.quotes.findIndex(
    (includedRateQuote) =>
      includedRateQuote.rateQuote.id === rateQuote.rateQuote.id,
  );

  if (index > 0) {
    config.value.quotes.splice(
      index - 1,
      2,
      config.value.quotes[index],
      config.value.quotes[index - 1],
    );
  }
};

const onMoveDown = (rateQuote: WaterfallTenderRateQuoteVm) => {
  const index = config.value.quotes.findIndex(
    (includedRateQuote) =>
      includedRateQuote.rateQuote.id === rateQuote.rateQuote.id,
  );

  if (index < config.value.quotes.length - 1) {
    config.value.quotes.splice(
      index,
      2,
      config.value.quotes[index + 1],
      config.value.quotes[index],
    );
  }
};

const onCancel = () => {
  isVisible.value = false;
};

const onBeginWaterfallTender = async () => {
  if (!(await validate())) {
    console.warn('Waterfall tender validation failed');
    return;
  }

  startingWaterfallTender.value = true;

  let sequenceIterator = 0;
  const configuration = new WaterfallTenderConfig({
    rateShopId: rateShop?.value?.data?.id,
    quotes: config.value.quotes.map((rateQuote) => {
      const isBroker =
        rateQuote.rateQuote.carrier?.type === CapacityProviderType.BROKER;
      const tenderMethod = getEffectiveTenderMethod(rateQuote);
      const tenderConfigOptions = isBroker
        ? (rateQuote.sendToBrokerageRequest?.tenderConfigOptions ??
          rateQuote.tenderConfigOptions)
        : rateQuote.tenderConfigOptions;

      return new WaterfallTenderRateQuoteConfig({
        rateQuoteId: rateQuote.rateQuote.id,
        tenderMethod,
        tenderConfigOptions,
        sequenceNumber: ++sequenceIterator,
      });
    }),
    excludedQuotes: config.value.excludedQuotes.map(
      (excludeQuote) =>
        new WaterfallTenderExcludedRateQuote({
          rateQuoteId: excludeQuote.rateQuote.id,
          tenderMethod: excludeQuote.tenderMethod,
          reason: excludeQuote.reason,
        }),
    ),
    tenderTimeoutDuration: config.value.tenderTimeoutDuration,
  });

  emit('waterfall-tender', {
    configuration,
    callback: (success: boolean) => {
      if (success) {
        isVisible.value = false;
      }
      startingWaterfallTender.value = false;
    },
  });
};

watch(includedRatesList, (newVal) => {
  if (newVal) {
    // I know this seems weird to call a "use" method like this, but this doesn't work if it's called before includedRatesList is mounted
    useSortable(includedRatesList, includedRates, {
      handle: '.drag-handle',
      chosenClass: 'grabbing',
      dragClass: 'dragging',
    });
  }
});

watch(
  isVisible,
  (newVal: boolean) => {
    if (newVal) {
      initializeWaterfallTenderCandidates();
      loadSendToBrokerageData();
    }
  },
  { immediate: true },
);
</script>

<style>
.waterfall-tender-modal.rygen-modal.p-dialog {
  &.performing-setup {
    .p-dialog-content {
      .modal-content {
        min-height: 55vh;

        div {
          display: flex;
          gap: var(--sp-2);
          justify-content: center;
          align-items: center;
        }
      }
    }
  }

  .p-message {
    margin-bottom: var(--sp-3);
  }

  .waterfall-tender-rates-list {
    display: flex;
    flex-direction: column;
    gap: var(--gap-1);
    margin: var(--sp-3) 0;
    padding: 0;

    li {
      & > .marker {
        min-width: 20px;
      }

      & > .action-wrapper {
        min-width: 75px;
      }

      &.grabbing {
        cursor: grabbing;
      }

      &.dragging {
        .waterfall-tender-rate-quote-card {
          border: 1px solid var(--p-primary-500);
        }
      }
    }
  }

  .p-dialog-footer .modal-footer {
    justify-content: space-between;
  }
}
</style>
